import type { BpuSheet, Criterion, Enterprise, Lot, ManualScores, OrphanLine, RefLine } from '../types';

// Critères par défaut — Prix auto (moins-disant), Technique + Délai manuels.
// La somme des pondérations doit faire 100.
const DEFAULT_CRITERIA: Criterion[] = [
  { id: 'prix', label: 'Prix', weight: 60, auto: true },
  { id: 'technique', label: 'Valeur technique', weight: 30, auto: false },
  { id: 'delai', label: 'Délai', weight: 10, auto: false },
];

const LOT1_SCORES: ManualScores = {
  Bouygues: { technique: 16, delai: 14 },
  Eiffage: { technique: 14, delai: 15 },
  Vinci: { technique: 11, delai: 12 },
};

const LOT2_SCORES: ManualScores = {
  Colas: { technique: 16, delai: 15 },
  Eurovia: { technique: 15, delai: 14 },
  Sotec: { technique: 10, delai: 13 },
};

export const MOCK_DATA: RefLine[] = [
  { id: '1', art: '1', designation: 'GROS ŒUVRE', level: 0, isSection: true },
  { id: '1.1', art: '1.1', designation: 'Fondations', level: 1, isSection: true },
  {
    id: '1.1.1', art: '1.1.1', designation: 'Terrassement terrain', level: 2,
    refQty: 120, refUnit: 'm³', refPU: 45,
    offers: {
      Bouygues: { designation: 'Terrassement en pleine masse y/c évacuation', qty: 120, unit: 'm³', pu: 48, total: 5760, status: 'matched', ecart: 6.7 },
      Eiffage: { designation: 'Terrassements généraux terrain naturel', qty: 115, unit: 'm³', pu: 42, total: 4830, status: 'matched', ecart: -6.7 },
      Vinci: { designation: 'Terrassement général pleine masse', qty: 120, unit: 'm³', pu: 32, total: 3840, status: 'alert', ecart: -28.9, alert: 'PU anormalement bas (-28.9%)' },
    },
  },
  {
    id: '1.1.2', art: '1.1.2', designation: 'Fouilles en rigoles', level: 2,
    refQty: 85, refUnit: 'm³', refPU: 38,
    offers: {
      Bouygues: { designation: 'Fouilles en rigoles et tranchées', qty: 85, unit: 'm³', pu: 41, total: 3485, status: 'matched', ecart: 7.9 },
      Eiffage: { designation: 'Fouilles en tranchées pour fondations', qty: 85, unit: 'm³', pu: 36, total: 3060, status: 'matched', ecart: -5.3 },
      Vinci: { designation: 'Fouilles en tranchées', qty: 85, unit: 'm³', pu: 26, total: 2210, status: 'alert', ecart: -31.6, alert: 'PU anormalement bas (-31.6%)' },
    },
  },
  {
    id: '1.1.3', art: '1.1.3', designation: 'Dalle béton 20cm', level: 2,
    refQty: 200, refUnit: 'm²', refPU: 65,
    offers: {
      Bouygues: { designation: 'Dallage béton ép. 20cm sur terre-plein', qty: 200, unit: 'm²', pu: 72, total: 14600, status: 'matched', ecart: 10.8, alert: 'Total annoncé 14 600 € ≠ 200×72 = 14 400 € (+200 €)' },
      Eiffage: { designation: 'Dalle portée BA ép. 20 cm', qty: 200, unit: 'm²', pu: 63, total: 12600, status: 'matched', ecart: -3.1 },
      Vinci: { designation: 'Dallage béton armé ép. 20', qty: 200, unit: 'm3', pu: 44, total: 8800, status: 'alert', ecart: -32.3, alert: 'PU anormalement bas (-32.3%)' },
    },
  },
  {
    id: '1.1.4', art: '1.1.4', designation: 'Film polyane', level: 2,
    refQty: 200, refUnit: 'm²', refPU: 8,
    offers: {
      Bouygues: { designation: 'Film polyéthylène sous dallage 200µ', qty: 200, unit: 'm²', pu: 9, total: 1800, status: 'matched', ecart: 12.5 },
      Eiffage: null,
      Vinci: { designation: 'Film polyéthylène 150µ', qty: 200, unit: 'm²', pu: 6, total: 1200, status: 'matched', ecart: -25.0 },
    },
  },
  { id: '1.2', art: '1.2', designation: 'Murs', level: 1, isSection: true },
  {
    id: '1.2.1', art: '1.2.1', designation: 'Murs en parpaings', level: 2,
    refQty: 340, refUnit: 'm²', refPU: 95,
    offers: {
      Bouygues: { designation: 'Maçonnerie agglos creux 20x20x50', qty: 340, unit: 'm²', pu: 102, total: 34680, status: 'matched', ecart: 7.4 },
      Eiffage: { designation: 'Murs en blocs béton creux ép. 20', qty: 340, unit: 'm²', pu: 91, total: 30940, status: 'matched', ecart: -4.2 },
      Vinci: { designation: 'Maçonnerie blocs béton 20', qty: 340, unit: 'm²', pu: 68, total: 23120, status: 'alert', ecart: -28.4, alert: 'PU anormalement bas (-28.4%)' },
    },
  },
  {
    id: '1.2.2', art: '1.2.2', designation: 'Chaînages béton', level: 2,
    refQty: 150, refUnit: 'ml', refPU: 55,
    offers: {
      Bouygues: { designation: 'Chaînages horizontaux BA section 20x20', qty: 150, unit: 'ml', pu: 58, total: 8700, status: 'suspect', ecart: 5.5, alert: 'Matching incertain — vérifiez la correspondance' },
      Eiffage: { designation: 'Longrines et chaînages BA', qty: 145, unit: 'ml', pu: 53, total: 7685, status: 'alert', ecart: -3.6, alert: 'Qté non conforme (145 vs 150)' },
      Vinci: { designation: 'Chaînages BA 20x20', qty: 150, unit: 'ml', pu: 38, total: 5700, status: 'alert', ecart: -30.9, alert: 'Offre anormalement basse' },
    },
  },
  {
    id: '1.2.3', art: '1.2.3', designation: 'Enduit extérieur', level: 2,
    refQty: 280, refUnit: 'm²', refPU: 42,
    offers: {
      Bouygues: { designation: 'Enduit monocouche extérieur ép. 15mm', qty: 280, unit: 'm²', pu: 44, total: 12320, status: 'matched', ecart: 4.8 },
      Eiffage: { designation: 'Enduit projeté façades ext. finition grattée', qty: 280, unit: 'm²', pu: 40, total: 11200, status: 'matched', ecart: -4.8 },
      Vinci: { designation: 'Enduit monocouche façade', qty: 280, unit: 'm²', pu: 28, total: 7840, status: 'alert', ecart: -33.3, alert: 'PU anormalement bas (-33.3%)' },
    },
  },
  { id: '2', art: '2', designation: 'MENUISERIES', level: 0, isSection: true },
  { id: '2.1', art: '2.1', designation: 'Fenêtres', level: 1, isSection: true },
  {
    id: '2.1.1', art: '2.1.1', designation: 'Fenêtre standard 100x120', level: 2,
    refQty: 12, refUnit: 'u', refPU: 450,
    offers: {
      Bouygues: { designation: 'Fenêtre PVC 2 vantaux OB 100x120 + VR', qty: 12, unit: 'u', pu: 480, total: 5760, status: 'matched', ecart: 6.7 },
      Eiffage: { designation: 'Menuiserie ext. PVC L100xH120 double vitrage', qty: 12, unit: 'u', pu: 435, total: 5220, status: 'matched', ecart: -3.3 },
      Vinci: { designation: 'Fenêtre PVC 100x120 DV', qty: 12, unit: 'ens', pu: 310, total: 3720, status: 'alert', ecart: -31.1, alert: 'PU anormalement bas (-31.1%)' },
    },
  },
  {
    id: '2.1.2', art: '2.1.2', designation: 'Porte-fenêtre 215x180', level: 2,
    refQty: 4, refUnit: 'u', refPU: 850,
    offers: {
      Bouygues: null,
      Eiffage: { designation: 'Porte-fenêtre coulissante alu 215x180', qty: 4, unit: 'u', pu: 820, total: 3500, status: 'matched', ecart: -3.5, alert: 'Total annoncé 3 500 € ≠ 4×820 = 3 280 € (+220 €)' },
      Vinci: { designation: 'Porte-fenêtre coulissante 215x180', qty: 4, unit: 'u', pu: 580, total: 2320, status: 'alert', ecart: -31.8, alert: 'PU anormalement bas (-31.8%)' },
    },
  },
  { id: '2.2', art: '2.2', designation: 'Portes', level: 1, isSection: true },
  {
    id: '2.2.1', art: '2.2.1', designation: 'Porte intérieure standard', level: 2,
    refQty: 8, refUnit: 'u', refPU: 320,
    offers: {
      Bouygues: { designation: 'Bloc-porte intérieur âme pleine 83cm + huisserie', qty: 8, unit: 'u', pu: 345, total: 2760, status: 'matched', ecart: 7.8 },
      Eiffage: { designation: 'Porte int. isoplane pré-peinte 83 + bâti métal', qty: 8, unit: 'u', pu: 310, total: 2480, status: 'matched', ecart: -3.1 },
      Vinci: null,
    },
  },
];

export const ORPHAN_LINES: OrphanLine[] = [
  { id: 'o1', enterprise: 'Bouygues', designation: 'Joints de dilatation périphériques mousse PE', qty: 45, unit: 'ml', pu: 32, total: 1440, suggestion: { refId: '1.2.3', refDesignation: 'Enduit extérieur', confidence: 0.4 } },
  { id: 'o2', enterprise: 'Eiffage', designation: 'Isolation sous dallage PSE ép. 80mm R=2.50', qty: 200, unit: 'm²', pu: 18, total: 3600, suggestion: { refId: '1.1.4', refDesignation: 'Film polyane', confidence: 0.7 } },
  { id: 'o3', enterprise: 'Eiffage', designation: 'Volet roulant PVC motorisé coffre tunnel', qty: 12, unit: 'u', pu: 210, total: 2520, suggestion: null },
];

export const ENTERPRISES: Enterprise[] = [
  { name: 'Bouygues', file: 'Offre_Bouygues_V2.xlsx', imported: '24 mars 2026', version: 2, matchRate: 92, totalHT: 89665, color: '#4F7BE8' },
  { name: 'Eiffage', file: 'Offre_Eiffage_V1.xlsx', imported: '22 mars 2026', version: 1, matchRate: 85, totalHT: 81295, color: '#E8724F' },
  { name: 'Vinci', file: 'Offre_Vinci_V1.xlsx', imported: '25 mars 2026', version: 1, matchRate: 78, totalHT: 59000, color: '#059669' },
];

// ---------- Lot 2 — VRD (BPU avec 2 DQE) ----------
// Catalogue BPU : refPU défini, les qty viennent de la DQE active, offer.total = qty×pu (recalculé à l'affichage).
const BPU_VRD_LINES: RefLine[] = [
  { id: 'v-1', art: '1', designation: 'VOIRIE', level: 0, isSection: true },
  { id: 'v-1.1', art: '1.1', designation: 'Enrobés', level: 1, isSection: true },
  {
    id: 'v-1.1.1', art: '1.1.1', designation: 'Enrobé BBSG 0/10 ép. 6cm', level: 2,
    refQty: 0, refUnit: 'm²', refPU: 35,
    offers: {
      Colas: { designation: 'Enrobé BBSG 0/10 fin 6cm', qty: 0, unit: 'm²', pu: 34, total: 0, status: 'matched', ecart: -2.9 },
      Eurovia: { designation: 'Couche roulement BBSG 0/10 e=6cm', qty: 0, unit: 'm²', pu: 36, total: 0, status: 'matched', ecart: 2.9 },
      Sotec: { designation: 'BBSG 6cm', qty: 0, unit: 'm²', pu: 24, total: 0, status: 'alert', ecart: -31.4, alert: 'PU anormalement bas (-31.4%)' },
    },
  },
  {
    id: 'v-1.1.2', art: '1.1.2', designation: 'Enrobé GB3 0/14 ép. 8cm', level: 2,
    refQty: 0, refUnit: 'm²', refPU: 42,
    offers: {
      Colas: { designation: 'Grave-bitume GB3 0/14 8cm', qty: 0, unit: 'm²', pu: 40, total: 0, status: 'matched', ecart: -4.8 },
      Eurovia: { designation: 'Couche base GB3 0/14 e=8cm', qty: 0, unit: 'm²', pu: 43, total: 0, status: 'matched', ecart: 2.4 },
      Sotec: { designation: 'GB3 8cm', qty: 0, unit: 'm²', pu: 28, total: 0, status: 'alert', ecart: -33.3, alert: 'PU anormalement bas (-33.3%)' },
    },
  },
  { id: 'v-1.2', art: '1.2', designation: 'Bordures et caniveaux', level: 1, isSection: true },
  {
    id: 'v-1.2.1', art: '1.2.1', designation: 'Bordure béton T2', level: 2,
    refQty: 0, refUnit: 'ml', refPU: 28,
    offers: {
      Colas: { designation: 'Bordure T2 y/c pose et fondation', qty: 0, unit: 'ml', pu: 29, total: 0, status: 'matched', ecart: 3.6 },
      Eurovia: { designation: 'Bordure béton T2 fournie posée', qty: 0, unit: 'ml', pu: 27, total: 0, status: 'matched', ecart: -3.6 },
      Sotec: { designation: 'Bordure T2', qty: 0, unit: 'u', pu: 19, total: 0, status: 'alert', ecart: -32.1, alert: 'PU anormalement bas (-32.1%)' },
    },
  },
  {
    id: 'v-1.2.2', art: '1.2.2', designation: 'Caniveau CC1 béton', level: 2,
    refQty: 0, refUnit: 'ml', refPU: 45,
    offers: {
      Colas: { designation: 'Caniveau CC1 béton coulé', qty: 0, unit: 'ml', pu: 47, total: 0, status: 'matched', ecart: 4.4 },
      Eurovia: { designation: 'Caniveau CC1 fourni posé', qty: 0, unit: 'ml', pu: 44, total: 0, status: 'matched', ecart: -2.2 },
      Sotec: { designation: 'Caniveau CC1', qty: 0, unit: 'ml', pu: 32, total: 0, status: 'alert', ecart: -28.9, alert: 'PU anormalement bas (-28.9%)' },
    },
  },
  { id: 'v-2', art: '2', designation: 'ASSAINISSEMENT', level: 0, isSection: true },
  { id: 'v-2.1', art: '2.1', designation: 'Réseau EP', level: 1, isSection: true },
  {
    id: 'v-2.1.1', art: '2.1.1', designation: 'Canalisation PVC CR8 DN200', level: 2,
    refQty: 0, refUnit: 'ml', refPU: 55,
    offers: {
      Colas: { designation: 'Canalisation PVC CR8 Ø200 y/c tranchée', qty: 0, unit: 'ml', pu: 53, total: 0, status: 'matched', ecart: -3.6 },
      Eurovia: { designation: 'Réseau PVC CR8 DN200 posé', qty: 0, unit: 'ml', pu: 56, total: 0, status: 'matched', ecart: 1.8 },
      Sotec: { designation: 'PVC DN200', qty: 0, unit: 'ml', pu: 38, total: 0, status: 'alert', ecart: -30.9, alert: 'PU anormalement bas (-30.9%)' },
    },
  },
  {
    id: 'v-2.1.2', art: '2.1.2', designation: 'Regard béton 80x80', level: 2,
    refQty: 0, refUnit: 'u', refPU: 380,
    offers: {
      Colas: { designation: 'Regard de visite béton 80x80 H=1,50m', qty: 0, unit: 'u', pu: 375, total: 0, status: 'matched', ecart: -1.3 },
      Eurovia: { designation: 'Regard 80x80 béton + tampon fonte', qty: 0, unit: 'u', pu: 385, total: 0, status: 'matched', ecart: 1.3 },
      Sotec: { designation: 'Regard 80', qty: 0, unit: 'u', pu: 270, total: 0, status: 'alert', ecart: -28.9, alert: 'PU anormalement bas (-28.9%)' },
    },
  },
  {
    id: 'v-2.1.3', art: '2.1.3', designation: 'Avaloir à grille', level: 2,
    refQty: 0, refUnit: 'u', refPU: 220,
    offers: {
      Colas: { designation: 'Avaloir à grille fonte y/c raccord', qty: 0, unit: 'u', pu: 225, total: 0, status: 'matched', ecart: 2.3 },
      Eurovia: { designation: 'Avaloir grille fonte C250', qty: 0, unit: 'u', pu: 218, total: 0, status: 'matched', ecart: -0.9 },
      Sotec: { designation: 'Avaloir grille', qty: 0, unit: 'u', pu: 155, total: 0, status: 'alert', ecart: -29.5, alert: 'PU anormalement bas (-29.5%)' },
    },
  },
];

const BPU_VRD_ORPHANS: OrphanLine[] = [
  { id: 'v-o1', enterprise: 'Colas', designation: 'Signalisation verticale panneaux B14', qty: 6, unit: 'u', pu: 180, total: 1080, suggestion: null },
];

const BPU_VRD_ENTERPRISES: Enterprise[] = [
  { name: 'Colas', file: 'BPU_Colas_V1.xlsx', imported: '26 mars 2026', version: 1, matchRate: 100, totalHT: 41860, color: '#DC2626' },
  { name: 'Eurovia', file: 'BPU_Eurovia_V1.xlsx', imported: '26 mars 2026', version: 1, matchRate: 100, totalHT: 43388, color: '#0891B2' },
  { name: 'Sotec', file: 'BPU_Sotec_V1.xlsx', imported: '27 mars 2026', version: 1, matchRate: 100, totalHT: 29260, color: '#F59E0B' },
];

const BPU_VRD_SHEETS: BpuSheet[] = [
  {
    id: 'dqe-base',
    name: 'DQE Base',
    quantities: {
      'v-1.1.1': 450, 'v-1.1.2': 280, 'v-1.2.1': 120, 'v-1.2.2': 85,
      'v-2.1.1': 95, 'v-2.1.2': 4, 'v-2.1.3': 6,
    },
  },
  {
    id: 'dqe-variante',
    name: 'DQE Variante parking',
    quantities: {
      'v-1.1.1': 680, 'v-1.1.2': 280, 'v-1.2.1': 180, 'v-1.2.2': 85,
      'v-2.1.1': 110, 'v-2.1.2': 5, 'v-2.1.3': 8,
    },
  },
];

export const MOCK_LOTS: Lot[] = [
  {
    id: 'lot-1',
    name: 'Lot 1 — Gros Œuvre + Menuiseries',
    type: 'DPGF',
    enterprises: ENTERPRISES,
    lines: MOCK_DATA,
    orphans: ORPHAN_LINES,
    criteria: DEFAULT_CRITERIA,
    manualScores: LOT1_SCORES,
  },
  {
    id: 'lot-2',
    name: 'Lot 2 — VRD',
    type: 'BPU',
    enterprises: BPU_VRD_ENTERPRISES,
    lines: BPU_VRD_LINES,
    orphans: BPU_VRD_ORPHANS,
    sheets: BPU_VRD_SHEETS,
    criteria: DEFAULT_CRITERIA,
    manualScores: LOT2_SCORES,
  },
];
